/**
 * FINMA References Card Component
 * TASK-213: Build Transaction Detail Modal
 *
 * REUSABLE component for displaying FINMA regulation references
 * Will be reused in compliance training pages, regulation lookup, and audit trails
 *
 * @component
 * @example
 * <FINMAReferencesCard
 *   references={finmaRefs}
 *   expandable={true}
 * />
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, BookOpen, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { FINMAReference } from '../../types/compliance';
import { toast } from 'sonner';

interface FINMAReferencesCardProps {
  /** List of FINMA regulation references */
  references: FINMAReference[];
  /** Whether articles should be expandable/collapsible */
  expandable?: boolean;
}

const FINMAReferencesCard: React.FC<FINMAReferencesCardProps> = ({
  references,
  expandable = true,
}) => {
  // Track expanded state for each reference
  const [expandedIds, setExpandedIds] = useState<Set<string>>(
    new Set(expandable ? [] : references.map((ref) => ref.article))
  );

  // Toggle expanded state for a reference
  const toggleExpanded = (article: string) => {
    if (!expandable) return;

    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(article)) {
        newSet.delete(article);
      } else {
        newSet.add(article);
      }
      return newSet;
    });
  };

  // Handle view full article click
  const handleViewArticle = (article: string) => {
    // TODO: Open full regulation view when backend is ready
    toast.info(
      'Full regulation view coming soon. This will link to the complete FINMA regulation text.'
    );
    console.log('View full article:', article);
  };

  if (references.length === 0) {
    return (
      <Card className="border border-gray-200">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-2">
            <BookOpen className="w-5 h-5 text-gray-500" />
            <h3 className="text-lg font-semibold text-gray-900">FINMA Regulations</h3>
          </div>
          <p className="text-sm text-gray-600">
            No specific regulations referenced for this transaction.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border border-gray-200">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <BookOpen className="w-5 h-5 text-indigo-600" />
          <h3 className="text-lg font-semibold text-gray-900">
            FINMA Regulations ({references.length})
          </h3>
        </div>

        <div className="space-y-3">
          {references.map((reference) => {
            const isExpanded = expandedIds.has(reference.article);

            return (
              <div
                key={reference.article}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                {/* Header */}
                <div
                  className={`p-4 bg-gray-50 ${
                    expandable ? 'cursor-pointer hover:bg-gray-100' : ''
                  } transition-colors`}
                  onClick={() => toggleExpanded(reference.article)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className="text-sm font-bold text-gray-900">
                        {reference.article}
                      </h4>
                      <p className="text-sm text-gray-700 mt-0.5">{reference.title}</p>
                    </div>
                    {expandable && (
                      <button
                        className="ml-2 p-1 hover:bg-gray-200 rounded transition-colors"
                        aria-label={isExpanded ? 'Collapse' : 'Expand'}
                      >
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-gray-600" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-600" />
                        )}
                      </button>
                    )}
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="p-4 bg-white space-y-3">
                    {/* Description */}
                    <div>
                      <h5 className="text-xs font-semibold text-gray-500 uppercase mb-1">
                        Description
                      </h5>
                      <p className="text-sm text-gray-700">{reference.description}</p>
                    </div>

                    {/* Requirement */}
                    <div>
                      <h5 className="text-xs font-semibold text-gray-500 uppercase mb-1">
                        Requirement
                      </h5>
                      <p className="text-sm text-gray-700">{reference.requirement}</p>
                    </div>

                    {/* View Full Article Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewArticle(reference.article);
                      }}
                      className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-800 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      View Full Article
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default FINMAReferencesCard;
